// ---------------------------------------------------------
// Datei: vaillantSerialNumber.ts
// Zweck: Validierung von Vaillant-Seriennummern (Außeneinheit) und geteilte
//        Formular-Ableitung (trimmed/formatValid/canSubmit) für die
//        Seriennummer-Modals.
// Kontext: getKnownAusseneinheitSerialNumber bündelt die kanonische
//          Prioritätsreihenfolge über alle Quellen hinweg (lokal bestätigt >
//          lokal gescannt > aktuelle Deal-Property > Legacy-Deal-Property,
//          Ticket #1276), damit Inbetriebnahmeprotokoll und Formulare
//          dieselbe Seriennummer vorbefüllen, auch offline.
// Stand: 2026-09-09
// ---------------------------------------------------------
import { useDocumentStore } from '../store/documentState';
import type { HubSpotDeal } from '../types/hubspot';

// Strikt: Produktionswoche 01–53, exakt 28 Zeichen
const STRICT = /^21\d{2}(0[1-9]|[1-4]\d|5[0-3])\d{10}\d{3}\d{6}\dN\d$/;

// Vereinfacht: nur Grundstruktur prüfen (z. B. bei leicht abweichenden Schreibweisen)
const SIMPLIFIED = /^21\d{24}N\d$/;

// Erkennt einen ISO-8601-Zeitstempel (z. B. "2026-09-09T12:34:56.789Z"). Vor Fix #1276/
// Re-Review wurde der Consent-Zeitstempel unter demselben Schlüssel wie die bestätigte
// Seriennummer abgelegt ('seriennummer-registrieren-text') — auf bereits im Feld befindlichen
// Geräten steht unter diesem Schlüssel deshalb weiterhin ein alter Zeitstempel statt einer
// Seriennummer. Dieser Guard verhindert, dass ein solcher Alt-Wert als Seriennummer verwendet wird.
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export type VaillantValidationResult =
  | { valid: true }
  | { valid: false; reason: string };

export interface VaillantSerialNumberFormState {
  trimmed: string;
  formatValid: boolean;
  canSubmit: boolean;
}

/**
 * Geteilte Ableitung von trimmed/formatValid/canSubmit für alle Seriennummer-Formulare
 * (SeriennummerModal, SeriennummerRegistrierenModal), damit sich die Submit-Bedingung
 * nicht in jedem Modal unabhängig entwickelt.
 */
export function getVaillantSerialNumberFormState(
  rawValue: string,
  options: { isChecking?: boolean } = {}
): VaillantSerialNumberFormState {
  const trimmed = rawValue.trim();
  const formatValid = trimmed.length === 0 || validateVaillantSerialNumber(trimmed).valid;
  const canSubmit = trimmed.length > 0 && formatValid && !options.isChecking;
  return { trimmed, formatValid, canSubmit };
}

/**
 * Ermittelt die bereits bekannte Seriennummer der Außeneinheit für einen Deal,
 * über alle Quellen hinweg (kanonische Prioritätsreihenfolge, Ticket #1276):
 *   1. lokal korrigierte/bestätigte Seriennummer aus "Seriennummer Registrieren"
 *      (Inbetriebnahme-Phase) — die verlässlichste Quelle, falls vorhanden
 *   2. lokal gescannte Seriennummer aus der Durchführung-Phase (Barcode-Scan),
 *      sofort per IndexedDB verfügbar, auch offline und vor jedem Deal-Sync
 *   3. aktuelle Deal-Property (`seriennummer_der_warmepumpe`)
 *   4. Legacy-Deal-Property (`seriennummer_der_ausseneinheit`)
 * Liest den Document-Store direkt über getState(), damit die Funktion auch
 * außerhalb von React-Komponenten (z. B. in FormularModal.loadTemplateWithData)
 * ohne Hook aufgerufen werden kann.
 *
 * Code-Review-Finding (PR #431/#432): der Schlüssel 'seriennummer-registrieren-text' wurde
 * nach erfolgreicher Registrierung mit dem Consent-Zeitstempel überschrieben (Zeitstempel
 * landete dadurch als vermeintliche Seriennummer in sn_ag). PhaseScreen.
 * performSeriennummerRegistrierenSubmit und queueService.processVaillantSerialRegister legen
 * den Consent-Zeitstempel deshalb jetzt unter dem separaten Schlüssel
 * 'seriennummer-registrieren-consent-text' ab — dieser Schlüssel hier enthält damit
 * ausschließlich die bestätigte Seriennummer.
 *
 * Re-Review-Finding (PR #431/#432): auf bereits vor diesem Fix genutzten Geräten steht unter
 * 'seriennummer-registrieren-text' im persistierten Store weiterhin ein alter Consent-Zeitstempel
 * (das Feld existierte vorher noch nicht getrennt). Ein solcher Alt-Wert wird deshalb per
 * ISO_TIMESTAMP_PATTERN erkannt und verworfen, statt ihn als Seriennummer zu verwenden.
 */
export function getKnownAusseneinheitSerialNumber(
  dealId: string,
  deal?: HubSpotDeal | null
): string {
  const { getDocumentData } = useDocumentStore.getState();
  const registeredValue = getDocumentData(`${dealId}-inbetriebnahme`, 'seriennummer-registrieren-text');
  const registeredIsUsable = !!registeredValue && !ISO_TIMESTAMP_PATTERN.test(registeredValue);
  return (
    (registeredIsUsable ? registeredValue : '')
    || getDocumentData(`${dealId}-durchfuehrung`, 'seriennummer-ausseneinheit-text')
    || deal?.seriennummer_der_warmepumpe
    || deal?.seriennummer_der_ausseneinheit
    || ''
  );
}

export function validateVaillantSerialNumber(value: string): VaillantValidationResult {
  const s = value.trim();

  if (s.length === 0) {
    return { valid: false, reason: 'Seriennummer ist leer.' };
  }

  if (STRICT.test(s)) {
    return { valid: true };
  }

  if (SIMPLIFIED.test(s)) {
    return { valid: true };
  }

  if (s.length !== 28) {
    return {
      valid: false,
      reason: `Erwartet 28 Zeichen, eingegeben: ${s.length}.`,
    };
  }

  if (!s.startsWith('21')) {
    return { valid: false, reason: 'Muss mit „21" beginnen.' };
  }

  if (s[26] !== 'N') {
    return { valid: false, reason: 'Zeichen 27 muss „N" sein.' };
  }

  return {
    valid: false,
    reason: 'Format entspricht nicht dem bekannten Vaillant-Muster.',
  };
}
